import React, { useState, useEffect, useCallback } from 'react';
import { TableSkeleton } from '../../components/enterprise';
import {
    Box, Typography, Paper, Button, Chip, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Alert, CircularProgress,
    IconButton, Tooltip, useTheme, Table, TableHead, TableRow, TableCell,
    TableBody, TableContainer, InputAdornment,
} from '@mui/material';
import {
    Add as AddIcon, Delete as DeleteIcon, Refresh as RefreshIcon,
    ContentCopy as CopyIcon, Autorenew as RotateIcon,
    Block as RevokeIcon,
    Router as GatewayIcon, Visibility as ViewIcon,
    VisibilityOff as HideIcon,
} from '@mui/icons-material';
import billingService from '../../services/billingService';
import { parseServerTs, toServerDate } from '../../timezone';

const statusColors = {
    ACTIVE: 'success',
    INACTIVE: 'default',
    REVOKED: 'error',
};

const GatewayManagementPage = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [gateways, setGateways] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Register dialog
    const [registerOpen, setRegisterOpen] = useState(false);
    const [newGateway, setNewGateway] = useState({ name: '', description: '' });
    const [registering, setRegistering] = useState(false);

    // Token display dialog (shown after registration or rotation)
    const [tokenDialog, setTokenDialog] = useState({ open: false, token: '', gatewayName: '' });
    const [tokenVisible, setTokenVisible] = useState(false);
    const [copied, setCopied] = useState(false);

    // Delete confirm dialog
    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: '' });

    // Rotate confirm dialog
    const [rotateDialog, setRotateDialog] = useState({ open: false, id: null, name: '' });

    const fetchGateways = useCallback(async () => {
        try {
            setLoading(true);
            const res = await billingService.listGateways();
            setGateways(res.data || []);
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to load gateways: ' + (err.response?.data?.error || err.message) });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchGateways(); }, [fetchGateways]);

    const handleRegister = async () => {
        if (!newGateway.name.trim()) return;
        try {
            setRegistering(true);
            const res = await billingService.registerGateway(newGateway);
            setRegisterOpen(false);
            setNewGateway({ name: '', description: '' });
            setTokenDialog({ open: true, token: res.data.token, gatewayName: res.data.name });
            setTokenVisible(false);
            setCopied(false);
            fetchGateways();
            setMessage({ type: 'success', text: `Gateway "${res.data.name}" registered successfully` });
        } catch (err) {
            setMessage({ type: 'error', text: 'Registration failed: ' + (err.response?.data?.error || err.message) });
        } finally {
            setRegistering(false);
        }
    };

    const handleRotateToken = async () => {
        try {
            const res = await billingService.rotateGatewayToken(rotateDialog.id);
            setRotateDialog({ open: false, id: null, name: '' });
            setTokenDialog({ open: true, token: res.data.token, gatewayName: res.data.name });
            setTokenVisible(false);
            setCopied(false);
            fetchGateways();
            setMessage({ type: 'success', text: 'Token rotated successfully' });
        } catch (err) {
            setMessage({ type: 'error', text: 'Token rotation failed: ' + (err.response?.data?.error || err.message) });
        }
    };

    const handleRevoke = async (id, name) => {
        try {
            await billingService.revokeGateway(id);
            fetchGateways();
            setMessage({ type: 'success', text: `Gateway "${name}" revoked` });
        } catch (err) {
            setMessage({ type: 'error', text: 'Revoke failed: ' + (err.response?.data?.error || err.message) });
        }
    };

    const handleDelete = async () => {
        try {
            await billingService.deleteGateway(deleteDialog.id);
            setDeleteDialog({ open: false, id: null, name: '' });
            fetchGateways();
            setMessage({ type: 'success', text: 'Gateway deleted' });
        } catch (err) {
            setMessage({ type: 'error', text: 'Delete failed: ' + (err.response?.data?.error || err.message) });
        }
    };

    const copyToken = () => {
        navigator.clipboard.writeText(tokenDialog.token);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const formatDate = (dt) => toServerDate(dt)?.toLocaleString() ?? '-';

    const formatRelative = (dt) => {
        const ts = parseServerTs(dt);
        if (!Number.isFinite(ts)) return 'Never';
        const diff = Date.now() - ts;
        if (diff < 60000) return 'Just now';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
        return `${Math.floor(diff / 86400000)}d ago`;
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1200 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>Gateway Management</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Register and manage gateways that authenticate to the platform for API key validation and tenant routing.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button startIcon={<RefreshIcon />} onClick={fetchGateways} disabled={loading}>
                        Refresh
                    </Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setRegisterOpen(true)}>
                        Register Gateway
                    </Button>
                </Box>
            </Box>

            {message.text && (
                <Alert severity={message.type} onClose={() => setMessage({ type: '', text: '' })} sx={{ mb: 2 }}>
                    {message.text}
                </Alert>
            )}

            {/* Gateway Table */}
            <Paper sx={{ bgcolor: isDark ? '#0d1117' : '#fff' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <TableSkeleton rows={5} columns={4} />
                    </Box>
                ) : gateways.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <GatewayIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                        <Typography color="text.secondary">No gateways registered</Typography>
                        <Typography variant="body2" color="text.disabled">
                            Register a gateway to allow it to authenticate and route telemetry data.
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Token Prefix</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Last Heartbeat</TableCell>
                                    <TableCell>ClickHouse writes</TableCell>
                                    <TableCell>Host / IP</TableCell>
                                    <TableCell>Version</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {gateways.map((gw) => (
                                    <TableRow key={gw.id} hover>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>{gw.name}</Typography>
                                                {gw.description && (
                                                    <Typography variant="caption" color="text.secondary">{gw.description}</Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontFamily="monospace" sx={{ color: 'text.secondary' }}>
                                                {gw.tokenPrefix}...
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={gw.status}
                                                color={statusColors[gw.status] || 'default'}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={formatDate(gw.lastHeartbeat)}>
                                                <Typography variant="body2" color={gw.lastHeartbeat ? 'text.primary' : 'text.disabled'}>
                                                    {formatRelative(gw.lastHeartbeat)}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            {gw.clickhouseWriteErrors > 0 ? (
                                                <Tooltip title={`${gw.clickhouseWriteErrors} ClickHouse write error(s); ${gw.clickhouseWritesOk || 0} ok — check the cluster credentials in Storage Clusters`}>
                                                    <Chip label="Failing" color="error" size="small" variant="outlined" />
                                                </Tooltip>
                                            ) : gw.clickhouseWritesOk > 0 ? (
                                                <Tooltip title={`${gw.clickhouseWritesOk} successful ClickHouse writes`}>
                                                    <Chip label="OK" color="success" size="small" variant="outlined" />
                                                </Tooltip>
                                            ) : (
                                                <Typography variant="body2" color="text.disabled">—</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {gw.hostname || gw.lastIp || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {gw.version || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={`Created by ${gw.createdBy || 'unknown'}`}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {formatDate(gw.createdAt)}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                <Tooltip title="Rotate Token">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setRotateDialog({ open: true, id: gw.id, name: gw.name })}
                                                        disabled={gw.status === 'REVOKED'}
                                                    >
                                                        <RotateIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                {gw.status === 'ACTIVE' ? (
                                                    <Tooltip title="Revoke">
                                                        <IconButton
                                                            size="small"
                                                            color="warning"
                                                            onClick={() => handleRevoke(gw.id, gw.name)}
                                                        >
                                                            <RevokeIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : gw.status !== 'ACTIVE' && gw.status !== 'REVOKED' ? null : null}
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => setDeleteDialog({ open: true, id: gw.id, name: gw.name })}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Register Gateway Dialog */}
            <Dialog open={registerOpen} onClose={() => setRegisterOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Register New Gateway</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        A unique authentication token will be generated. You must copy it immediately — it will not be shown again.
                    </Typography>
                    <TextField
                        fullWidth label="Gateway Name" required
                        value={newGateway.name}
                        onChange={(e) => setNewGateway(prev => ({ ...prev, name: e.target.value }))}
                        sx={{ mb: 2 }}
                        placeholder="e.g. production-gateway-us-east"
                    />
                    <TextField
                        fullWidth label="Description" multiline rows={2}
                        value={newGateway.description}
                        onChange={(e) => setNewGateway(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Optional description of this gateway's purpose or location"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRegisterOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained" onClick={handleRegister}
                        disabled={!newGateway.name.trim() || registering}
                        startIcon={registering ? <CircularProgress size={16} /> : <AddIcon />}
                    >
                        Register
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Token Display Dialog */}
            <Dialog open={tokenDialog.open} onClose={() => setTokenDialog({ open: false, token: '', gatewayName: '' })} maxWidth="sm" fullWidth>
                <DialogTitle>Gateway Token — {tokenDialog.gatewayName}</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Copy this token now. It will not be shown again. If lost, you must rotate to generate a new one.
                    </Alert>
                    <TextField
                        fullWidth
                        label="Gateway Token"
                        value={tokenDialog.token}
                        type={tokenVisible ? 'text' : 'password'}
                        InputProps={{
                            readOnly: true,
                            sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setTokenVisible(!tokenVisible)} size="small">
                                        {tokenVisible ? <HideIcon /> : <ViewIcon />}
                                    </IconButton>
                                    <IconButton onClick={copyToken} size="small" color={copied ? 'success' : 'default'}>
                                        <CopyIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {copied && (
                        <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                            Copied to clipboard
                        </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Set this token in your gateway's configuration:
                    </Typography>
                    <Paper sx={{ p: 1.5, mt: 1, bgcolor: isDark ? '#161b22' : '#f6f8fa', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        gateway.token={tokenVisible ? tokenDialog.token : '••••••••••••••••'}
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => setTokenDialog({ open: false, token: '', gatewayName: '' })}>
                        Done
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Rotate Token Confirm Dialog */}
            <Dialog open={rotateDialog.open} onClose={() => setRotateDialog({ open: false, id: null, name: '' })} maxWidth="xs" fullWidth>
                <DialogTitle>Rotate Token</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        This will invalidate the current token for "{rotateDialog.name}". The gateway will need to be reconfigured with the new token.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRotateDialog({ open: false, id: null, name: '' })}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={handleRotateToken} startIcon={<RotateIcon />}>
                        Rotate Token
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirm Dialog */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null, name: '' })} maxWidth="xs" fullWidth>
                <DialogTitle>Delete Gateway</DialogTitle>
                <DialogContent>
                    <Alert severity="error">
                        Permanently delete gateway "{deleteDialog.name}"? This cannot be undone and the gateway will immediately lose access.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, id: null, name: '' })}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDelete} startIcon={<DeleteIcon />}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default GatewayManagementPage;
